// @ts-check
// Resumo de cada bloco do bot para o canvas, a prévia somente leitura da galeria, as estatísticas e o modo
// demonstração (contrato de bots, seções 2.1 e 22.2). Puro: não traduz nem consulta a API. Devolve o título e uma
// lista de partes no mesmo formato do resumo dos fluxos (chave de texto com parâmetros ou texto pronto, com destaque),
// para o cartão do canvas dos fluxos desenhar igual; quem desenha traduz as chaves do namespace `bots`. Os nomes que
// dependem de dados (departamento, membro, outro bot, bloco de destino, etapa, campo) chegam pelas funções de consulta;
// sem elas, o resumo cai no texto genérico do tipo.
import { BOT_NODE_CATEGORIES } from './bot-definition.js';

/**
 * @typedef {import('./bot-definition.js').BotNode} BotNode
 * @typedef {import('./bot-definition.js').BotNodeType} BotNodeType
 * @typedef {{ text?: string, key?: string, params?: Record<string, string | number>, strong?: boolean, attach?: boolean }} BotSummaryPart
 * @typedef {{ titleKey: string, name: string | null, category: string | null, icon: string, parts: BotSummaryPart[], isUnconfigured: boolean,
 *   badge: { key: string, tone: 'marketing' | 'service' } | null }} BotNodeSummary
 *   name: o nome dado ao bloco (vence o título do tipo); isUnconfigured: o resumo é o chamado "Escreva a mensagem"
 * @typedef {{
 *   teamName?: (id: string) => string | null,
 *   memberName?: (id: string) => string | null,
 *   botName?: (id: string) => string | null,
 *   nodeTitle?: (nodeId: string) => string | null,
 *   stageLabel?: (value: string) => string | null,
 *   fieldLabel?: (target: string, fieldName: string) => string | null,
 *   bookingPage?: (id: string) => { name: string, durationMinutes: number } | null,
 * }} BotSummaryLookups
 */

/** Ícone de cada bloco (nomes de client/src/ui/icon-nodes.js). */
export const BOT_NODE_ICONS = Object.freeze(/** @type {Record<BotNodeType, string>} */ ({
  SEND_MESSAGE: 'IconMessage', ASK: 'IconMessageQuestion',
  CONDITION: 'IconHierarchy2', SPLIT_PERCENT: 'IconPercentage', SET_VARIABLE: 'IconBraces', GOTO: 'IconCornerUpLeft', SUBBOT: 'IconRobot',
  END: 'IconPlayerStop',
  IDENTIFY_GUARDIAN: 'IconUserSearch', CHOOSE_STUDENT: 'IconSchool', STRONG_VERIFICATION: 'IconShieldCheck',
  SET_PURPOSE: 'IconTargetArrow', OPEN_TICKET: 'IconTicket', HANDOFF: 'IconHeadset',
  CREATE_CAPTURE_CARD: 'IconUserPlus', UPDATE_STAGE: 'IconProgressCheck', UPDATE_FIELD: 'IconEdit', ADD_TAGS: 'IconTags', REMOVE_TAGS: 'IconTags',
  ASSIGN_OWNER: 'IconUserCheck', CREATE_TASK: 'IconChecklist', ADD_NOTE: 'IconNotes', SEND_NOTIFICATION: 'IconBell', SCHEDULE_VISIT: 'IconCalendarEvent',
  CALL_WEBHOOK: 'IconWebhook', HTTP_REQUEST: 'IconApi',
}));

const EXCERPT_LENGTH = 60;

/** Chave do título do bloco pelo tipo. @param {{ type: string }} node */
export const botNodeTitleKey = (node) => (node?.type && node.type in BOT_NODE_ICONS ? `bots.nodes.${node.type}.name` : 'bots.nodes.UNKNOWN.name');

/** @param {string} key @param {Record<string, string | number>} [params] @returns {BotSummaryPart} */
const keyPart = (key, params) => (params ? { key, params } : { key });

/** @param {string} text @param {boolean} [strong] @returns {BotSummaryPart} */
const textPart = (text, strong = false) => (strong ? { text, strong: true } : { text });

/** @param {unknown} value */
const textOf = (value) => (typeof value === 'string' && value.trim() !== '' ? value.trim() : null);

/** Começo do texto, numa linha, com reticências quando passa do tamanho. @param {string} text */
export function botTextExcerpt(text) {
  const flat = text.replace(/\s+/g, ' ').trim();
  return flat.length > EXCERPT_LENGTH ? `${flat.slice(0, EXCERPT_LENGTH - 1).trimEnd()}…` : flat;
}

/** Nome por consulta, ou o texto "(removido)". @param {string | null | undefined} id @param {((id: string) => string | null) | undefined} lookup */
function namedPart(id, lookup) {
  if (!id) return null;
  const name = lookup?.(id) ?? null;
  return name ? textPart(name, true) : keyPart('bots.summary.missing');
}

/** Destino da transferência em partes. @param {Record<string, any> | null | undefined} target @param {BotSummaryLookups} lookups @returns {BotSummaryPart[]} */
export function handoffTargetParts(target, lookups) {
  if (!target?.kind) return [];
  if (target.kind === 'TEAM') {
    const part = namedPart(target.teamId, lookups.teamName);
    return part ? [keyPart('bots.summary.HANDOFF.team'), part] : [];
  }
  if (target.kind === 'MEMBER') {
    const part = namedPart(target.workspaceMemberId, lookups.memberName);
    return part ? [keyPart('bots.summary.HANDOFF.member'), part] : [];
  }
  if (target.kind === 'CARD_OWNER') return [keyPart('bots.summary.HANDOFF.cardOwner')];
  const count = Array.isArray(target.workspaceMemberIds) ? target.workspaceMemberIds.length : 0;
  return count > 0 ? [keyPart('bots.summary.HANDOFF.roundRobin', { count })] : [];
}

/**
 * Resumo de um bloco: título, partes do texto, selo e se ainda falta configurar.
 * @param {BotNode} node @param {BotSummaryLookups} [lookups]
 * @returns {BotNodeSummary}
 */
export function summarizeBotNode(node, lookups = {}) {
  const config = node?.config ?? {};
  const type = /** @type {BotNodeType} */ (node?.type);
  /** @type {BotNodeSummary} */
  const summary = {
    titleKey: botNodeTitleKey(node),
    name: textOf(node?.name),
    category: BOT_NODE_CATEGORIES[type] ?? null,
    icon: BOT_NODE_ICONS[type] ?? 'IconHelpCircle',
    parts: [],
    isUnconfigured: false,
    badge: null,
  };
  /** @param {string} key @param {Record<string, string | number>} [params] */
  const unconfigured = (key, params) => {
    summary.parts = [keyPart(key, params)];
    summary.isUnconfigured = true;
    return summary;
  };
  switch (type) {
    case 'SEND_MESSAGE': {
      const text = textOf(config.text);
      if (!text && !config.media?.fileId) return unconfigured('bots.summary.SEND_MESSAGE.empty');
      if (text) summary.parts.push(textPart(botTextExcerpt(text)));
      const options = config.options;
      if (options?.source === 'STATIC') {
        const count = (Array.isArray(options.items) ? options.items.length : 0) + (options.addHumanOption ? 1 : 0);
        summary.parts.push(keyPart(options.presentation === 'LIST' ? 'bots.summary.SEND_MESSAGE.list' : 'bots.summary.SEND_MESSAGE.buttons', { count }));
      } else if (options?.source) {
        summary.parts.push(keyPart('bots.summary.SEND_MESSAGE.dynamic'), keyPart(`bots.optionSources.${options.source}`));
      } else if (config.media?.fileId) summary.parts.push(keyPart(`bots.summary.SEND_MESSAGE.media.${config.media.kind ?? 'DOCUMENT'}`));
      return summary;
    }
    case 'ASK': {
      const text = textOf(config.text);
      const variable = textOf(config.saveTo?.variable);
      if (!text) return unconfigured('bots.summary.ASK.empty');
      summary.parts.push(textPart(botTextExcerpt(text)));
      if (variable) summary.parts.push(keyPart('bots.summary.ASK.saveTo', { variable }));
      return summary;
    }
    case 'CONDITION': {
      const rules = Array.isArray(config.rules) ? config.rules : [];
      if (rules.length === 0) return unconfigured('bots.summary.CONDITION.empty');
      summary.parts.push(keyPart(config.logic === 'OR' ? 'bots.summary.CONDITION.any' : 'bots.summary.CONDITION.all', { count: rules.length }));
      return summary;
    }
    case 'SPLIT_PERCENT': {
      const shares = config.shares && typeof config.shares === 'object' ? config.shares : {};
      const values = ['a', 'b', 'c', 'd'].filter((key) => typeof shares[key] === 'number').map((key) => `${shares[key]}%`);
      if (values.length < 2) return unconfigured('bots.summary.SPLIT_PERCENT.empty');
      summary.parts.push(textPart(values.join(' / '), true));
      return summary;
    }
    case 'SET_VARIABLE': {
      const name = textOf(config.name);
      if (!name) return unconfigured('bots.summary.SET_VARIABLE.empty');
      summary.parts.push(textPart(`bot.${name}`, true), keyPart('bots.summary.SET_VARIABLE.receives'), textPart(botTextExcerpt(String(config.value ?? ''))));
      return summary;
    }
    case 'GOTO': {
      if (!config.targetNodeId) return unconfigured('bots.summary.GOTO.empty');
      summary.parts.push(keyPart('bots.summary.GOTO.target'), namedPart(config.targetNodeId, lookups.nodeTitle) ?? keyPart('bots.summary.missing'));
      return summary;
    }
    case 'SUBBOT': {
      if (!config.botId) return unconfigured('bots.summary.SUBBOT.empty');
      summary.parts.push(keyPart('bots.summary.SUBBOT.starts'), namedPart(config.botId, lookups.botName) ?? keyPart('bots.summary.missing'));
      return summary;
    }
    case 'END': {
      const text = textOf(config.text);
      summary.parts.push(text ? textPart(botTextExcerpt(text)) : keyPart('bots.summary.END.silent'));
      return summary;
    }
    case 'IDENTIFY_GUARDIAN':
      summary.parts.push(keyPart(config.allowWeakData === false ? 'bots.summary.IDENTIFY_GUARDIAN.phoneOnly' : 'bots.summary.IDENTIFY_GUARDIAN.weakData'));
      return summary;
    case 'CHOOSE_STUDENT':
      summary.parts.push(keyPart('bots.summary.CHOOSE_STUDENT.topic'), keyPart(`bots.topics.${config.topic ?? 'GENERAL'}`));
      if (config.allowWholeFamily !== false) summary.parts.push(keyPart('bots.summary.CHOOSE_STUDENT.wholeFamily'));
      return summary;
    case 'STRONG_VERIFICATION':
      summary.parts.push(keyPart(`bots.verificationPurposes.${config.purpose ?? 'SENSITIVE_TOPIC'}`));
      return summary;
    case 'SET_PURPOSE': {
      if (!config.purpose) return unconfigured('bots.summary.SET_PURPOSE.empty');
      summary.parts.push(keyPart(`bots.threadPurposes.${config.purpose}`));
      const team = namedPart(config.teamId, lookups.teamName);
      if (team) summary.parts.push(keyPart('bots.summary.SET_PURPOSE.team'), team);
      return summary;
    }
    case 'OPEN_TICKET': {
      const team = namedPart(config.teamId, lookups.teamName);
      if (!team) return unconfigured('bots.summary.OPEN_TICKET.empty');
      summary.parts.push(keyPart('bots.summary.OPEN_TICKET.team'), team);
      if (config.ticketType === 'RETENTION') summary.badge = { key: 'bots.summary.OPEN_TICKET.retention', tone: 'service' };
      return summary;
    }
    case 'HANDOFF': {
      const parts = handoffTargetParts(config.target, lookups);
      if (parts.length === 0) return unconfigured('bots.summary.HANDOFF.empty');
      summary.parts.push(...parts);
      return summary;
    }
    case 'CREATE_CAPTURE_CARD':
      summary.parts.push(keyPart(config.assign === 'ROUND_ROBIN' ? 'bots.summary.CREATE_CAPTURE_CARD.roundRobin' : 'bots.summary.CREATE_CAPTURE_CARD.noOwner'));
      return summary;
    case 'UPDATE_STAGE': {
      if (!config.stage) return unconfigured('bots.summary.UPDATE_STAGE.empty');
      summary.parts.push(keyPart('bots.summary.UPDATE_STAGE.to'), textPart(lookups.stageLabel?.(config.stage) ?? config.stage, true));
      return summary;
    }
    case 'UPDATE_FIELD': {
      if (!config.fieldName) return unconfigured('bots.summary.UPDATE_FIELD.empty');
      summary.parts.push(textPart(lookups.fieldLabel?.(config.target ?? 'CARD', config.fieldName) ?? config.fieldName, true));
      return summary;
    }
    case 'ADD_TAGS':
    case 'REMOVE_TAGS': {
      const tags = Array.isArray(config.tags) ? config.tags.filter((tag) => typeof tag === 'string' && tag) : [];
      if (tags.length === 0) return unconfigured('bots.summary.TAGS.empty');
      summary.parts.push(textPart(tags.join(', '), true));
      return summary;
    }
    case 'ASSIGN_OWNER': {
      const count = Array.isArray(config.workspaceMemberIds) ? config.workspaceMemberIds.length : 0;
      if (count === 0) return unconfigured('bots.summary.ASSIGN_OWNER.empty');
      summary.parts.push(keyPart(config.strategy === 'ROUND_ROBIN' ? 'bots.summary.ASSIGN_OWNER.roundRobin' : 'bots.summary.ASSIGN_OWNER.specific', { count }));
      return summary;
    }
    case 'CREATE_TASK': {
      const title = textOf(config.title);
      if (!title) return unconfigured('bots.summary.CREATE_TASK.empty');
      summary.parts.push(textPart(botTextExcerpt(title), true));
      return summary;
    }
    case 'ADD_NOTE': {
      const body = textOf(config.body);
      if (!body) return unconfigured('bots.summary.ADD_NOTE.empty');
      summary.parts.push(textPart(botTextExcerpt(body)));
      return summary;
    }
    case 'SEND_NOTIFICATION': {
      const title = textOf(config.title);
      if (!title) return unconfigured('bots.summary.SEND_NOTIFICATION.empty');
      summary.parts.push(textPart(botTextExcerpt(title), true));
      return summary;
    }
    case 'SCHEDULE_VISIT': {
      if (typeof config.bookingPageId !== 'string' || !config.bookingPageId) return unconfigured('bots.summary.SCHEDULE_VISIT.empty');
      // sem a lista das páginas (ainda carregando) o cartão fica só com o título
      if (!lookups.bookingPage) return summary;
      const page = lookups.bookingPage(config.bookingPageId);
      if (!page) {
        summary.parts.push(keyPart('bots.summary.missing'));
        return summary;
      }
      summary.parts.push(textPart(page.name, true), keyPart('bots.summary.SCHEDULE_VISIT.duration', { minutes: page.durationMinutes }));
      return summary;
    }
    case 'CALL_WEBHOOK':
    case 'HTTP_REQUEST': {
      const host = hostOf(config.url);
      if (!host) return unconfigured('bots.summary.INTEGRATION.empty');
      summary.parts.push(type === 'HTTP_REQUEST' ? textPart(`${config.method ?? 'POST'} ${host}`, true) : textPart(host, true));
      return summary;
    }
    default:
      return unconfigured('bots.summary.unknown');
  }
}

/** @param {unknown} url */
function hostOf(url) {
  if (typeof url !== 'string' || url.trim() === '') return null;
  try {
    return new URL(url).host || null;
  } catch {
    return null;
  }
}

/**
 * Resumo numa linha (leitor de tela e lista de pendências).
 * @param {BotNodeSummary} summary @param {(key: string, params?: Record<string, unknown>) => string} translate
 */
export function botSummaryText(summary, translate) {
  return summary.parts.map((part, position) => {
    const text = part.key ? translate(part.key, part.params ?? {}) : part.text ?? '';
    return position > 0 && !part.attach ? ` ${text}` : text;
  }).join('').trim();
}

/** Título do bloco: o nome dado ou o do tipo. @param {BotNodeSummary} summary @param {(key: string) => string} translate */
export const botSummaryTitle = (summary, translate) => summary.name ?? translate(summary.titleKey);
